import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { isEmpty } from 'lodash-es';

import { DEFAULT_DATE_TIME_FORMAT } from '@iconik-io/core/src/date/date_formats';
import type { AssetsGetShareSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { SettingsMergedSettingsSchemaNullable } from '@iconik-io/iconik-api/src/services/settings';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

const watermarkSettingsDefaults = {
  show_watermark: false,
  include_date_time: true,
  include_email: true,
  include_ip_address: true,
  include_custom_text: true,
  custom_text: '',
  text_appearance: 'center',
  text_opacity: 0.7,
  shadow_opacity: 0.7,
  show_for_groups: 'all',
  show_in_context: 'proxies',
} as const;

const getWatermarkSettings = (
  settings: SettingsMergedSettingsSchemaNullable,
) => {
  let watermarkSettings = settings.watermark_options || {};
  const keys = Object.keys(watermarkSettingsDefaults);

  keys.forEach((item) => {
    // biome-ignore lint/plugin: keys come from watermarkSettingsDefaults itself, but Object.keys is always typed string[].
    const key = item as keyof typeof watermarkSettingsDefaults;
    if (watermarkSettings?.[key] === undefined) {
      watermarkSettings = {
        ...watermarkSettings,
        [key]: watermarkSettingsDefaults[key],
      };
    }
  });

  return watermarkSettings;
};

export const resolveShowWatermark = (
  baseShow: boolean | null | undefined,
  showForGroups: string | null | undefined,
  configuredGroups: string[] | null | undefined,
  userGroups: string[] | null | undefined,
  userTeams: string[] | null | undefined,
): boolean => {
  if (!baseShow) {
    return false;
  }
  if (!showForGroups || showForGroups === 'all') {
    return true;
  }
  const memberIds = [...(userGroups ?? []), ...(userTeams ?? [])];
  const targetGroups = configuredGroups ?? [];
  const isInGroup = targetGroups.some((id) => memberIds.includes(id));
  if (showForGroups === 'selected_only') {
    return isInGroup;
  }
  if (showForGroups === 'except') {
    return !isInGroup;
  }
  return true;
};

export const useWatermark = ({
  systemSettings,
  userEmail,
  userGroups,
  userTeams,
  shareSettings,
  handleGetSystemSettings,
  isPreview = false,
}: {
  systemSettings: SettingsMergedSettingsSchemaNullable;
  userEmail: string | null | undefined;
  userGroups?: string[] | null;
  userTeams?: string[] | null;
  shareSettings: AssetsGetShareSchemaWithIdNullable;
  handleGetSystemSettings?: () => void;
  isPreview?: boolean;
}) => {
  const { store } = useBasePlayerContext();

  const systemWatermarkSettings = getWatermarkSettings(systemSettings);
  const hasShareSettings = !isPreview && Object.keys(shareSettings).length > 0;
  const [watermarkSettings, setWatermarkSettings] = useState(
    systemWatermarkSettings,
  );

  useEffect(() => {
    if (isEmpty(systemSettings)) {
      handleGetSystemSettings?.();
      return;
    }
    setWatermarkSettings(getWatermarkSettings(systemSettings));
  }, [systemSettings, handleGetSystemSettings]);

  useEffect(() => {
    const showInContext = watermarkSettings.show_in_context ?? 'proxies';
    const isSharesOnlyContext = showInContext === 'shares';

    if (isSharesOnlyContext && !hasShareSettings && !isPreview) {
      store.setWatermarks([]);
      return;
    }

    const baseShow = hasShareSettings
      ? shareSettings.show_watermark
      : watermarkSettings.show_watermark;
    const shouldShowWatermark =
      hasShareSettings || isPreview
        ? Boolean(baseShow)
        : resolveShowWatermark(
            baseShow,
            watermarkSettings.show_for_groups,
            watermarkSettings.groups,
            userGroups,
            userTeams,
          );
    if (!shouldShowWatermark) {
      store.setWatermarks([]);
      return;
    }
    const watermarks = [];
    if (
      watermarkSettings.include_date_time ||
      watermarkSettings.include_email ||
      watermarkSettings.include_ip_address
    ) {
      const dynamicText = [
        watermarkSettings.include_date_time
          ? format(new Date(), DEFAULT_DATE_TIME_FORMAT)
          : '',
        watermarkSettings.include_email ? userEmail : '',
        watermarkSettings.include_ip_address ? systemSettings.client_ip : '',
      ].join(' ');
      watermarks.push(dynamicText);
    }
    if (
      watermarkSettings.include_custom_text &&
      watermarkSettings.custom_text
    ) {
      const customText = watermarkSettings.custom_text.split('\n');
      watermarks.push(...customText);
    }
    store.setWatermarks(watermarks);
  }, [
    watermarkSettings.show_watermark,
    watermarkSettings.show_for_groups,
    watermarkSettings.groups,
    watermarkSettings.show_in_context,
    isPreview,
    userGroups,
    userTeams,
    watermarkSettings.include_date_time,
    watermarkSettings.include_email,
    watermarkSettings.include_ip_address,
    watermarkSettings.include_custom_text,
    watermarkSettings.custom_text,
    systemSettings.client_ip,
    userEmail,
    shareSettings.show_watermark,
    hasShareSettings,
  ]);

  useEffect(() => {
    if (
      watermarkSettings.text_appearance !== undefined &&
      watermarkSettings.text_opacity !== undefined &&
      watermarkSettings.shadow_opacity !== undefined
    ) {
      store.setWatermarkOptions({
        position: watermarkSettings.text_appearance,
        textOpacity: watermarkSettings.text_opacity,
        shadowOpacity: watermarkSettings.shadow_opacity,
      });
    }
  }, [
    watermarkSettings.text_appearance,
    watermarkSettings.text_opacity,
    watermarkSettings.shadow_opacity,
  ]);

  return [watermarkSettings, setWatermarkSettings] as const;
};
